import { Node, Edge } from 'reactflow';
import { FlowNode } from '@holloway/dsl';
import { getNodeColor } from '@/features/editor/nodes';

/**
 * Check if two nodes intersect based on their positions and dimensions
 */
export function nodesIntersect(nodeA: Node, nodeB: Node): boolean {
  const nodeAWidth = (nodeA.width || 200);
  const nodeAHeight = (nodeA.height || 100);
  const nodeBWidth = (nodeB.width || 200);
  const nodeBHeight = (nodeB.height || 100);

  const nodeARight = nodeA.position.x + nodeAWidth;
  const nodeABottom = nodeA.position.y + nodeAHeight;
  const nodeBRight = nodeB.position.x + nodeBWidth;
  const nodeBBottom = nodeB.position.y + nodeBHeight;

  return !(
    nodeARight < nodeB.position.x ||
    nodeA.position.x > nodeBRight ||
    nodeABottom < nodeB.position.y ||
    nodeA.position.y > nodeBBottom
  );
}

/**
 * Map DSL node types to ReactFlow custom node types
 */
export function getReactFlowNodeType(dslNodeType: string): string {
  const typeMap: Record<string, string> = {
    'Page': 'page',
    'ContactUs': 'contactUs',
    'Action': 'action',
    'Note': 'note',
    'If': 'if',
    'Branch': 'branch',
    'DecisionPoint': 'decisionPoint',
    'Start': 'start',
    'End': 'end',
  };
  return typeMap[dslNodeType] || 'default';
}

/**
 * Create stub nodes and edges for branching elements
 */
/**
 * Create stub nodes and edges for branching elements
 */
function createBranchStubsAndEdges(
  dslNode: FlowNode,
  branchingElement: any,
  nodes: Node[],
  edges: Edge[],
  parentId: string
) {
  if (branchingElement.type === 'YesNo' && branchingElement.branches) {
    const yesTarget = branchingElement.branches.yes;
    const noTarget = branchingElement.branches.no;
    const yesLabel = branchingElement.yesLabel || 'Yes';
    const noLabel = branchingElement.noLabel || 'No';

    // Handle Yes branch
    if (yesTarget) {
      edges.push({
        id: `${parentId}-yes-${yesTarget}`,
        source: parentId,
        target: yesTarget,
        label: yesLabel,
        style: { stroke: '#10b981', strokeWidth: 2 },
      });
    } else {
      const stubId = `${parentId}-stub-yes`;
      nodes.push({
        id: stubId,
        type: 'branchStub',
        position: {
          x: (dslNode.position?.x || 0) - 120,
          y: (dslNode.position?.y || 0) + 150,
        },
        data: { branchLabel: yesLabel, parentId: parentId, branchType: 'yes' },
      });
      edges.push({
        id: `${parentId}-yes-${stubId}`,
        source: parentId,
        target: stubId,
        label: yesLabel,
        style: { stroke: '#10b981', strokeWidth: 2, strokeDasharray: '5,5' },
      });
    }

    // Handle No branch
    if (noTarget) {
      edges.push({
        id: `${parentId}-no-${noTarget}`,
        source: parentId,
        target: noTarget,
        label: noLabel,
        style: { stroke: '#ef4444', strokeWidth: 2 },
      });
    } else {
      const stubId = `${parentId}-stub-no`;
      nodes.push({
        id: stubId,
        type: 'branchStub',
        position: {
          x: (dslNode.position?.x || 0) + 120,
          y: (dslNode.position?.y || 0) + 150,
        },
        data: { branchLabel: noLabel, parentId: parentId, branchType: 'no' },
      });
      edges.push({
        id: `${parentId}-no-${stubId}`,
        source: parentId,
        target: stubId,
        label: noLabel,
        style: { stroke: '#ef4444', strokeWidth: 2, strokeDasharray: '5,5' },
      });
    }
  }

  if (branchingElement.type === 'MultiChoice' && branchingElement.branches) {
    branchingElement.branches.forEach((branch: any, idx: number) => {
      if (branch.targetPageId) {
        edges.push({
          id: `${parentId}-choice${idx}-${branch.targetPageId}`,
          source: parentId,
          target: branch.targetPageId,
          label: branch.option,
          style: { stroke: '#6366f1', strokeWidth: 2 },
        });
      } else {
        const stubId = `${parentId}-stub-choice${idx}`;
        const totalBranches = branchingElement.branches!.length;
        const spacing = 180;
        const startOffset = -((totalBranches - 1) * spacing) / 2;

        nodes.push({
          id: stubId,
          type: 'branchStub',
          position: {
            x: (dslNode.position?.x || 0) + startOffset + (idx * spacing),
            y: (dslNode.position?.y || 0) + 150,
          },
          data: { branchLabel: branch.option, parentId: parentId, branchType: 'choice', branchIndex: idx },
        });
        edges.push({
          id: `${parentId}-choice${idx}-${stubId}`,
          source: parentId,
          target: stubId,
          label: branch.option,
          style: { stroke: '#6366f1', strokeWidth: 2, strokeDasharray: '5,5' },
        });
      }
    });
  }

  if (branchingElement.type === 'Button') {
    const targetPageId = branchingElement.targetPageId;
    const label = branchingElement.buttonText || branchingElement.label || 'Button';

    if (targetPageId) {
      edges.push({
        id: `${parentId}-button-${branchingElement.id}-${targetPageId}`,
        source: parentId,
        target: targetPageId,
        label,
        style: { stroke: '#415A91', strokeWidth: 2 },
      });
    } else {
      const stubId = `${parentId}-stub-button-${branchingElement.id}`;
      nodes.push({
        id: stubId,
        type: 'branchStub',
        position: {
          x: dslNode.position?.x || 0,
          y: (dslNode.position?.y || 0) + 150,
        },
        data: { branchLabel: label, parentId, branchType: 'button', elementId: branchingElement.id },
      });
      edges.push({
        id: `${parentId}-button-${branchingElement.id}-${stubId}`,
        source: parentId,
        target: stubId,
        label,
        style: { stroke: '#415A91', strokeWidth: 2, strokeDasharray: '5,5' },
      });
    }
  }
}

/**
 * Convert FlowDSL nodes to ReactFlow nodes and edges
 */
export function convertDSLToReactFlowNodes(dslNodes: FlowNode[]): { nodes: Node[]; edges: Edge[] } {
  const nodes: Node[] = [];
  const edges: Edge[] = [];

  dslNodes.forEach((dslNode, index) => {
    // Ensure ID exists
    const nodeId = dslNode.id || `node-${index}-${Date.now()}`;

    // Use saved position if available, otherwise auto-layout
    const position = dslNode.position || {
      x: 100 + (index % 5) * 200,
      y: 100 + Math.floor(index / 5) * 150,
    };

    // Create ReactFlow node
    const node: Node = {
      id: nodeId,
      type: getReactFlowNodeType(dslNode.type),
      position,
      data: { ...dslNode, id: nodeId, color: getNodeColor(dslNode.type) },
    };
    nodes.push(node);

    // Create edges based on 'next' property
    if ('next' in dslNode && dslNode.next) {
      edges.push({
        id: `${nodeId}-${dslNode.next}`,
        source: nodeId,
        target: dslNode.next as string,
      });
    }

    // Handle Switch node with multiple cases
    if (dslNode.type === 'Switch' && 'cases' in dslNode) {
      dslNode.cases?.forEach((caseItem: any, caseIndex: number) => {
        if (caseItem.next) {
          edges.push({
            id: `${nodeId}-case${caseIndex}-${caseItem.next}`,
            source: nodeId,
            target: caseItem.next,
            label: `case ${caseIndex + 1}`,
          });
        }
      });
      if ('defaultNext' in dslNode && dslNode.defaultNext) {
        edges.push({
          id: `${nodeId}-default-${dslNode.defaultNext}`,
          source: nodeId,
          target: dslNode.defaultNext as string,
          label: 'default',
        });
      }
    }

    // Handle If node
    if (dslNode.type === 'If' && ('then' in dslNode || 'else' in dslNode)) {
      if ('then' in dslNode && dslNode.then) {
        edges.push({
          id: `${nodeId}-true-${dslNode.then}`,
          source: nodeId,
          sourceHandle: 'true',
          target: dslNode.then as string,
          label: 'true',
        });
      }
      if ('else' in dslNode && dslNode.else) {
        edges.push({
          id: `${nodeId}-false-${dslNode.else}`,
          source: nodeId,
          sourceHandle: 'false',
          target: dslNode.else as string,
          label: 'false',
        });
      }
    }

    // Handle Branch node with multiple paths
    if (dslNode.type === 'Branch' && 'branches' in dslNode) {
      dslNode.branches?.forEach((branch: any, branchIndex: number) => {
        if (branch.target) {
          edges.push({
            id: `${nodeId}-branch${branchIndex}-${branch.target}`,
            source: nodeId,
            sourceHandle: `branch-${branchIndex}`,
            target: branch.target,
            label: branch.label || `Branch ${branchIndex + 1}`,
          });
        }
      });
    }

    // Handle Page node with branching elements
    if (dslNode.type === 'Page' && 'elements' in dslNode) {
      const branchingElements = (dslNode.elements || []).filter((el: any) =>
        el.createBranches === true ||
        (el.type === 'Button' && el.action === 'navigate')
      );
      branchingElements.forEach((branchingElement: any) => {
        createBranchStubsAndEdges(dslNode, branchingElement, nodes, edges, nodeId);
      });
    }
  });

  return { nodes, edges };
}
